import type { ChatMessage, ChatSource, WidgetConfig } from '../api/client';
import { renderMarkdown } from './markdown';

export interface ChatUICallbacks {
  onSendMessage: (message: string) => void;
  onClose: () => void;
}

/** Handle for incrementally rendering a streaming assistant message. */
export interface StreamingMessage {
  /** Replace the message body with the (markdown) text rendered so far. */
  update: (fullText: string) => void;
  /** Render the source citations beneath the message. */
  setSources: (sources: ChatSource[]) => void;
}

export class ChatUI {
  private container: HTMLElement;
  private messagesContainer: HTMLElement;
  private input: HTMLInputElement;
  private sendButton: HTMLButtonElement;
  private callbacks: ChatUICallbacks;

  constructor(
    parent: HTMLElement,
    config: WidgetConfig,
    callbacks: ChatUICallbacks
  ) {
    this.callbacks = callbacks;
    this.container = this.createChatContainer(config);
    this.messagesContainer = this.container.querySelector(
      '.apidocs-widget-messages'
    )!;
    this.input = this.container.querySelector('.apidocs-widget-input')!;
    this.sendButton = this.container.querySelector('.apidocs-widget-send')!;

    this.setupEventListeners();
    parent.appendChild(this.container);

    // Add welcome message
    if (config.welcomeMessage) {
      this.addMessage({ role: 'assistant', content: config.welcomeMessage });
    }
  }

  private createChatContainer(config: WidgetConfig): HTMLElement {
    const container = document.createElement('div');
    container.className = 'apidocs-widget-chat';
    container.innerHTML = `
      <div class="apidocs-widget-header">
        <span>${config.name}</span>
        <div class="apidocs-widget-header-actions">
          <button class="apidocs-widget-expand" aria-label="Expand" title="Expand">
            <svg class="apidocs-widget-icon-expand" viewBox="0 0 24 24">
              <path d="M9 3H3v6h2V5h4V3zm6 0v2h4v4h2V3h-6zM5 15H3v6h6v-2H5v-4zm14 4h-4v2h6v-6h-2v4z"/>
            </svg>
            <svg class="apidocs-widget-icon-collapse" viewBox="0 0 24 24">
              <path d="M5 5h4V3H3v6h2V5zm14 0v4h2V3h-6v2h4zM5 19v-4H3v6h6v-2H5zm14 0h-4v2h6v-6h-2v4z"/>
            </svg>
          </button>
          <button class="apidocs-widget-close" aria-label="Close">&times;</button>
        </div>
      </div>
      <div class="apidocs-widget-messages"></div>
      <div class="apidocs-widget-input-container">
        <input
          type="text"
          class="apidocs-widget-input"
          placeholder="${config.placeholder}"
        />
        <button class="apidocs-widget-send" aria-label="Send" title="Send message">
          <svg viewBox="0 0 24 24">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </button>
      </div>
      <div class="apidocs-widget-hint">Press <kbd>Enter</kbd> to send</div>
    `;
    return container;
  }

  private setupEventListeners(): void {
    const closeButton = this.container.querySelector('.apidocs-widget-close')!;
    closeButton.addEventListener('click', () => this.callbacks.onClose());

    const expandButton = this.container.querySelector('.apidocs-widget-expand')!;
    expandButton.addEventListener('click', () => this.toggleExpand());

    this.sendButton.addEventListener('click', () => this.handleSend());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    });
  }

  private handleSend(): void {
    const message = this.input.value.trim();
    if (message) {
      this.callbacks.onSendMessage(message);
      this.input.value = '';
    }
  }

  open(): void {
    this.container.classList.add('open');
    this.input.focus();
  }

  close(): void {
    this.container.classList.remove('open');
  }

  toggle(): void {
    if (this.container.classList.contains('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  private toggleExpand(): void {
    const expanded = this.container.classList.toggle('expanded');
    const button = this.container.querySelector('.apidocs-widget-expand')!;
    button.setAttribute('aria-label', expanded ? 'Collapse' : 'Expand');
    button.setAttribute('title', expanded ? 'Collapse' : 'Expand');
    this.input.focus();
  }

  addMessage(message: ChatMessage): void {
    const messageEl = document.createElement('div');
    messageEl.className = `apidocs-widget-message ${message.role}`;
    if (message.role === 'assistant') {
      messageEl.innerHTML = renderMarkdown(message.content);
    } else {
      messageEl.textContent = message.content;
    }
    this.messagesContainer.appendChild(messageEl);
    this.scrollToBottom();
  }

  /**
   * Creates an empty assistant message bubble and returns a handle for streaming
   * text into it and rendering its sources once complete.
   */
  startAssistantMessage(): StreamingMessage {
    const messageEl = document.createElement('div');
    messageEl.className = 'apidocs-widget-message assistant';
    this.messagesContainer.appendChild(messageEl);
    this.scrollToBottom();

    return {
      update: (fullText: string) => {
        messageEl.innerHTML = renderMarkdown(fullText);
        this.scrollToBottom();
      },
      setSources: (sources: ChatSource[]) => {
        if (!sources.length) return;
        messageEl.appendChild(this.buildSourcesElement(sources));
        this.scrollToBottom();
      },
    };
  }

  // Builds a sources footer using DOM nodes (no innerHTML) so titles/URLs from
  // the server can't inject markup.
  private buildSourcesElement(sources: ChatSource[]): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'apidocs-widget-sources';

    const label = document.createElement('div');
    label.className = 'apidocs-widget-sources-label';
    label.textContent = 'Sources';
    wrap.appendChild(label);

    for (const source of sources) {
      const item = document.createElement('div');
      item.className = 'apidocs-widget-source';
      if (source.url && /^https?:\/\//i.test(source.url)) {
        const link = document.createElement('a');
        link.href = source.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = source.title;
        item.appendChild(link);
      } else {
        item.textContent = source.title;
      }
      wrap.appendChild(item);
    }
    return wrap;
  }

  showLoading(): void {
    const loading = document.createElement('div');
    loading.className = 'apidocs-widget-loading';
    loading.innerHTML = '<span></span><span></span><span></span>';
    loading.id = 'apidocs-loading';
    this.messagesContainer.appendChild(loading);
    this.scrollToBottom();
  }

  hideLoading(): void {
    const loading = document.getElementById('apidocs-loading');
    if (loading) {
      loading.remove();
    }
  }

  setInputEnabled(enabled: boolean): void {
    this.input.disabled = !enabled;
    this.sendButton.disabled = !enabled;
  }

  private scrollToBottom(): void {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }
}
